/**
 * ARC Library (DC-1735, B2) — the live /arc-library surface.
 *
 * Graduates the accepted prototype's structure (left nav of key processes →
 * families, use-case detail with controls → tests, attribution everywhere)
 * into the product, reading the ENTITLED organization's compiled catalog
 * from /api/arc-library — no hardcoded statistics, no standalone HTML
 * runtime; every count derives from the payload.
 *
 * Two navigable views of the same registry (8/4 session AC):
 *  - FAMILY view — Key Process → Subprocess (how banks organize; "if you
 *    ever lose yourself, you go back to the traditional context");
 *  - AI USE-CASE pivot — grouped by the provisional ROSI category overlay,
 *    with an honest "Not yet categorized" bucket (never guessed).
 *
 * Honesty + IP rules carried in the markup:
 *  - Content renders ONLY when the org is entitled; otherwise the page says
 *    so and (for admins) offers activation. No teaser content.
 *  - Attribution is visible at page level and per overlay element: ARC
 *    framework & content — A. Philo's pre-existing IP; evidence legs and
 *    categories — ROSI overlay, provisional.
 *  - A control with no tests renders an honest "No tests in this catalog
 *    version"; nothing is fabricated.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import './banking-use-cases.css';
import './banking-board.css';

interface LibTest {
  id: string;
  testCode: string;
  name: string;
  testingCategory: string | null;
  regulationsReferenced: string | null;
  primaryRegulators: string | null;
  severity: string | null;
  evidenceText: string | null;
  evidenceLegs: string[];
}

interface LibControl {
  id: string;
  controlCode: string;
  name: string;
  category: string | null;
  description: string | null;
  scopeLevel: string;
  bundleScope: string | null;
  regulators: string | null;
  officialStandards: string | null;
  raci: { raciRole: string; party: string }[];
  tests: LibTest[];
}

interface LibUseCase {
  id: string;
  keyProcess: string;
  subprocess: string;
  subprocessPurpose: string | null;
  name: string;
  goals: string | null;
  modelDescription: string | null;
  keyRisks: string | null;
  systemicRisk: string | null;
  rosiCategory: string | null;
  controls: LibControl[];
}

interface LibVersion {
  id: string;
  versionLabel: string;
  sourceLabel: string;
  contentDigest: string;
  useCaseCount: number;
  controlCount: number;
  testCount: number;
  riskCount: number;
}

interface LibTree {
  version: LibVersion;
  useCases: LibUseCase[];
  bundleControls: LibControl[];
  risks: {
    id: string;
    riskCode: string;
    subprocess: string | null;
    /**
     * The control this risk is answered by, when the catalog states one.
     *
     * The server has always sent this (`BrowseRisk.controlCode`) but the client
     * type omitted it, so the field was invisible to anything reading this
     * declaration — which is why the connection map concluded no risk→control
     * linkage existed and drew none. 209 of 267 rows carry it and every one
     * resolves to a real control. Null on subprocess-level rows (CS-SP-*),
     * which are genuinely unlinked and must never be threaded.
     */
    controlCode: string | null;
    riskLevel: string | null;
    label: string;
    systemic: boolean;
    impacts: { impact: string; systemic: boolean }[];
  }[];
}

interface LibStatus {
  entitled: boolean;
  entitledVersion: LibVersion | null;
  availableVersions: { id: string; versionLabel: string; sourceLabel: string }[];
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'not_entitled'; status: LibStatus }
  | { phase: 'ready'; tree: LibTree };

/** DC-1791 FR-1.3 — the compile report persisted with the catalog version. */
interface CompileReportRow {
  rowNumber: number;
  controlRef: string;
  name: string;
  subprocess: string | null;
  weightText: string | null;
}

interface CompileReport {
  sourceRowCount: number;
  seedable: { count: number; byBandForm: Record<string, number> };
  unseedable: { count: number; rows: CompileReportRow[]; weightSplit: Record<string, number> };
  legIngestion: { source: string; classifiedTests: number; unseedableEntries: number };
}

type ReportState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'error' }
  /** `report: null` = the version predates the report-carrying compiler. */
  | { phase: 'loaded'; report: CompileReport | null };

type ViewMode = 'family' | 'pivot';

const UNCATEGORIZED = 'Not yet categorized';

/**
 * Orientation strip — the ARC hierarchy, stated continuously.
 *
 * ARC nests five entity types (bank function → AI use case → control → test →
 * evidence leg) and the Library rendered all of them without ever drawing the
 * nesting. The only place the model was stated was the empty-state card, which
 * vanished on first click — so the teaching disappeared at exactly the moment
 * the reader began using the product. This states it on every screen and marks
 * how far the reader has walked: reached steps name what was reached, the
 * current step is emphasised, downstream steps stay visibly pending so the
 * remaining depth is legible rather than a surprise.
 */
const ORIENT_STEPS = [
  { key: 'function', label: 'Function' },
  { key: 'usecase', label: 'Use case' },
  { key: 'control', label: 'Control' },
  { key: 'test', label: 'Test' },
  { key: 'evidence', label: 'Evidence' },
] as const;

const OrientationStrip: React.FC<{
  useCase: LibUseCase;
  openControlCode: string | null;
}> = ({ useCase, openControlCode }) => {
  // How deep the reader currently is: a use case is always selected here, and
  // opening a control walks one step further. Test/evidence remain pending
  // until the control is expanded, which is where they become visible.
  const depth = openControlCode ? 2 : 1;
  const value: Record<string, string> = {
    function: useCase.keyProcess,
    usecase: useCase.subprocess,
    control: openControlCode ?? 'open one below',
    test: '—',
    evidence: '—',
  };

  return (
    <nav className="arc-orientation" data-testid="arc-orientation" aria-label="You are here">
      {ORIENT_STEPS.map((step, i) => {
        const state = i < depth ? 'reached' : i === depth ? 'current' : 'pending';
        return (
          <React.Fragment key={step.key}>
            {i > 0 && (
              <span className="arc-orient-sep" aria-hidden="true">
                ›
              </span>
            )}
            <span
              className="arc-orient-step"
              data-testid={`arc-orient-${step.key}`}
              data-state={state}
            >
              <span className="arc-orient-k">{step.label}</span>
              <span className="arc-orient-v">{value[step.key]}</span>
            </span>
          </React.Fragment>
        );
      })}
    </nav>
  );
};

/**
 * Connection map (D-BANK-UX-02, PM #12) — the Library's signature element,
 * disciplined: control nodes are real keyboard-focusable buttons exposing
 * pinned state via aria-pressed; ONE thread renders at a time (hover
 * previews, click pins) so a default wire tangle is structurally impossible;
 * relationships are many-to-many and the caption says so. Risks render as a
 * register column WITHOUT drawn linkage — the catalog carries no explicit
 * risk→control mapping, and the map never fabricates one.
 */
const ConnectionMap: React.FC<{
  useCase: LibUseCase;
  bundleControls: LibControl[];
  risks: LibTree['risks'];
}> = ({ useCase, bundleControls, risks }) => {
  const controls = useMemo(
    () => [...useCase.controls, ...bundleControls],
    [useCase, bundleControls]
  );
  const [pinned, setPinned] = useState<string | null>(controls[0]?.controlCode ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [wire, setWire] = useState<string>('');

  useEffect(() => {
    setPinned(controls[0]?.controlCode ?? null);
    setPreview(null);
  }, [controls]);

  const active = preview ?? pinned;
  const activeControl = controls.find((c) => c.controlCode === active) ?? null;
  const activeLegs = useMemo(() => {
    const legs = new Set<string>();
    for (const test of activeControl?.tests ?? []) {
      for (const leg of test.evidenceLegs) legs.add(leg);
    }
    return legs;
  }, [activeControl]);
  const allLegs = useMemo(() => {
    const legs = new Set<string>();
    for (const control of controls) {
      for (const test of control.tests) {
        for (const leg of test.evidenceLegs) legs.add(leg);
      }
    }
    return [...legs].sort();
  }, [controls]);
  const allStandards = useMemo(() => {
    const standards = new Set<string>();
    for (const control of controls) {
      if (control.regulators) standards.add(control.regulators);
      if (control.officialStandards) standards.add(control.officialStandards);
    }
    return [...standards].sort();
  }, [controls]);
  const activeStandards = useMemo(() => {
    const standards = new Set<string>();
    if (activeControl?.regulators) standards.add(activeControl.regulators);
    if (activeControl?.officialStandards) standards.add(activeControl.officialStandards);
    return standards;
  }, [activeControl]);
  /**
   * The family's risks split by whether the CATALOG links them to a control.
   *
   * Two kinds ship in ARC v6 and they must not be conflated:
   *  - control-scoped rows (CS1-01, CS3-02 …) carry an explicit `controlCode`
   *    that resolves to a real control — a linkage the catalog states, so the
   *    map draws it and the row participates in the thread;
   *  - subprocess-level rows (CS-SP-01 …) carry no control at all. They stay
   *    register-only and are never threaded, because inventing a linkage for
   *    them would be fabrication.
   *
   * An earlier revision took `.slice(0, 6)` of the family's rows, which
   * happened to catch exactly the unlinked CS-SP-* block, and concluded from
   * that sample that "the catalog carries no explicit risk→control linkage."
   * It does: 209 rows carry one and every one resolves. The sample was wrong,
   * not the catalog.
   */
  const familyRisks = useMemo(
    () => risks.filter((r) => r.subprocess === useCase.subprocess),
    [risks, useCase]
  );
  const controlCodes = useMemo(
    () => new Set(controls.map((c) => c.controlCode)),
    [controls]
  );
  const linkedRisks = useMemo(
    () => familyRisks.filter((r) => r.controlCode && controlCodes.has(r.controlCode)),
    [familyRisks, controlCodes]
  );
  /*
   * Three populations, not two. The earlier split was "linked to this use
   * case" vs "everything else", which merged genuinely code-less subprocess
   * rows with rows naming a SIBLING use case's control — then described the
   * merged set as "names no control", false for the second group. At real
   * scale that column reached 33 rows for one Credit Underwriting use case,
   * 28 of them siblings. Separating them caps it at 5 catalog-wide.
   */
  const registerRisks = useMemo(
    () => familyRisks.filter((r) => !r.controlCode),
    [familyRisks]
  );
  /** Answered by a control on another use case in this family — not this view's. */
  const siblingRisks = useMemo(
    () => familyRisks.filter((r) => r.controlCode && !controlCodes.has(r.controlCode)),
    [familyRisks, controlCodes]
  );

  // One wire: active control → its first leg → its first standard.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !active) {
      setWire('');
      return;
    }
    const anchor = (selector: string): { l: number; r: number; y: number } | null => {
      const el = panel.querySelector<HTMLElement>(selector);
      if (!el) return null;
      const pr = panel.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return null;
      return { l: r.left - pr.left, r: r.right - pr.left, y: r.top - pr.top + r.height / 2 };
    };
    const points = [
      anchor(`[data-map-risk="${active}"]`),
      anchor(`[data-map-control="${active}"]`),
      anchor('[data-map-leg][data-thread="on"]'),
      anchor('[data-map-standard][data-thread="on"]'),
    ].filter((p): p is { l: number; r: number; y: number } => p !== null);
    if (points.length < 2) {
      setWire('');
      return;
    }
    let d = '';
    for (let i = 0; i < points.length - 1; i += 1) {
      const a = points[i];
      const b = points[i + 1];
      const mid = (a.r + b.l) / 2;
      d += `M ${a.r - 4} ${a.y} C ${mid} ${a.y + 6} ${mid} ${b.y + 6} ${b.l + 4} ${b.y} `;
    }
    setWire(d);
  }, [active, activeLegs, activeStandards]);

  return (
    <div className="arc-connection-panel" ref={panelRef} data-testid="connection-map">
      <div className="arc-connection-head">
        <h3 className="arc-section-head">How {useCase.subprocess} connects</h3>
        <span className="arc-connection-hint">
          hover a risk or control to preview its thread · click to pin · one thread at a time
        </span>
      </div>
      <p className="arc-connection-why">
        Read left to right: a <strong>risk</strong> the bank carries is answered by a{' '}
        <strong>control</strong> ARC requires, proven by evidence drawn from a{' '}
        <strong>source system</strong>, and defensible against a named{' '}
        <strong>regulatory standard</strong>. Four columns, one thread — that is the whole model.
      </p>
      <svg className="arc-connection-wires" data-testid="connection-wires" aria-hidden="true">
        {wire && <path d={wire} />}
      </svg>
      <div className="arc-connection-cols">
        <div className="arc-connection-col">
          <p className="arc-connection-ch">Risks</p>
          {linkedRisks.map((risk) => (
            <button
              key={risk.id}
              type="button"
              className="arc-connection-node arc-connection-risk"
              data-map-risk={risk.controlCode ?? undefined}
              data-thread={active === risk.controlCode ? 'on' : 'off'}
              aria-pressed={pinned === risk.controlCode}
              onClick={() => setPinned(risk.controlCode)}
              onMouseEnter={() => setPreview(risk.controlCode)}
              onMouseLeave={() => setPreview(null)}
              title={`${risk.riskCode} — answered by control ${risk.controlCode}`}
            >
              <span className="arc-connection-code">{risk.riskCode}</span> {risk.label}
            </button>
          ))}
          {registerRisks.length > 0 && (
            <>
              {registerRisks.map((risk) => (
                <span key={risk.id} className="arc-connection-node arc-connection-muted">
                  <span className="arc-connection-code">{risk.riskCode}</span> {risk.label}
                </span>
              ))}
              <p className="arc-connection-footnote" data-testid="arc-register-only-note">
                {registerRisks.length} register-only{' '}
                {registerRisks.length === 1 ? 'row names' : 'rows name'} no control — shown, never
                threaded
              </p>
            </>
          )}
          {siblingRisks.length > 0 && (
            <p className="arc-connection-footnote" data-testid="arc-sibling-risk-note">
              {siblingRisks.length} further risk{siblingRisks.length === 1 ? '' : 's'} in{' '}
              {useCase.subprocess} {siblingRisks.length === 1 ? 'is' : 'are'} answered by controls on
              other use cases in this function — open those use cases to thread them.
            </p>
          )}
          {familyRisks.length === 0 && (
            <span className="arc-not-assigned">No register rows for this function.</span>
          )}
        </div>
        <div className="arc-connection-col">
          <p className="arc-connection-ch">Controls</p>
          {controls.map((control) => (
            <button
              key={control.id}
              type="button"
              className="arc-connection-node arc-connection-control"
              data-map-control={control.controlCode}
              data-thread={active === control.controlCode ? 'on' : 'off'}
              aria-pressed={pinned === control.controlCode}
              onClick={() => setPinned(control.controlCode)}
              onMouseEnter={() => setPreview(control.controlCode)}
              onMouseLeave={() => setPreview(null)}
            >
              {control.controlCode} {control.name}
              {control.scopeLevel === 'bundle' ? ' · FAMILY' : ''}
            </button>
          ))}
        </div>
        <div className="arc-connection-col">
          <p className="arc-connection-ch">Evidence legs (ROSI overlay)</p>
          {allLegs.length > 0 ? (
            allLegs.map((leg) => (
              <span
                key={leg}
                className="arc-connection-node"
                data-map-leg
                data-thread={activeLegs.has(leg) ? 'on' : 'off'}
              >
                {leg}
              </span>
            ))
          ) : (
            <span className="arc-not-assigned">not yet assigned</span>
          )}
        </div>
        <div className="arc-connection-col">
          <p className="arc-connection-ch">Regulatory standards</p>
          {allStandards.map((standard) => (
            <span
              key={standard}
              className="arc-connection-node"
              data-map-standard
              data-thread={activeStandards.has(standard) ? 'on' : 'off'}
            >
              {standard}
            </span>
          ))}
        </div>
      </div>
      <p className="arc-connection-note">
        These are <strong>many-to-many</strong> relationships — a control can serve several
        use cases and a standard grounds several tests. Pinning shows one thread honestly; it
        never implies a one-to-one chain.
      </p>
    </div>
  );
};

export const ArcLibrary: React.FC = () => {
  const { get, post } = useApiClient();
  const { isAdmin } = usePermissions();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [view, setView] = useState<ViewMode>('family');
  const [query, setQuery] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(null);
  const [openControl, setOpenControl] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [chosenVersion, setChosenVersion] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportState, setReportState] = useState<ReportState>({ phase: 'idle' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const status = await get<{ data?: LibStatus }>('/api/arc-library/status');
      if (!status.data?.entitled) {
        setState({ phase: 'not_entitled', status: status.data! });
        return;
      }
      const browse = await get<{ data?: { library?: LibTree } }>('/api/arc-library/browse');
      if (!browse.data?.library) {
        setState({ phase: 'error' });
        return;
      }
      setState({ phase: 'ready', tree: browse.data.library });
    } catch {
      setState({ phase: 'error' });
    }
  }, [get]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleReport = useCallback(async () => {
    const opening = !reportOpen;
    setReportOpen(opening);
    if (!opening || reportState.phase === 'loaded' || reportState.phase === 'loading') return;
    setReportState({ phase: 'loading' });
    try {
      const res = await get<{ data?: { report?: CompileReport | null } }>(
        '/api/arc-library/compile-report'
      );
      setReportState({ phase: 'loaded', report: res.data?.report ?? null });
    } catch {
      setReportState({ phase: 'error' });
    }
  }, [get, reportOpen, reportState.phase]);

  const activate = useCallback(async () => {
    if (!chosenVersion) return;
    setActivating(true);
    setActivateError(null);
    try {
      await post('/api/arc-library/entitlement', { catalogVersionId: chosenVersion });
      await load();
    } catch {
      setActivateError('The catalog version could not be activated.');
    } finally {
      setActivating(false);
    }
  }, [post, chosenVersion, load]);

  const tree = state.phase === 'ready' ? state.tree : null;

  const filteredUseCases = useMemo(() => {
    if (!tree) return [];
    const q = query.trim().toLowerCase();
    if (!q) return tree.useCases;
    return tree.useCases.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.subprocess.toLowerCase().includes(q) ||
        u.controls.some((c) => c.controlCode.toLowerCase().includes(q))
    );
  }, [tree, query]);

  /** FAMILY view: Key Process → Subprocess → use cases. */
  const familyGroups = useMemo(() => {
    const byProcess = new Map<string, Map<string, LibUseCase[]>>();
    for (const uc of filteredUseCases) {
      const families = byProcess.get(uc.keyProcess) ?? new Map<string, LibUseCase[]>();
      const list = families.get(uc.subprocess) ?? [];
      list.push(uc);
      families.set(uc.subprocess, list);
      byProcess.set(uc.keyProcess, families);
    }
    return [...byProcess.entries()].map(([keyProcess, families]) => ({
      keyProcess,
      families: [...families.entries()].map(([subprocess, useCases]) => {
        // Per-family summary so a collapsed row still answers "how big is this
        // and how far has the overlay reached" without being expanded.
        // Coverage is MEASURED — tests carrying at least one ROSI evidence leg
        // over total tests — so a family the overlay has not reached reads as
        // visibly thin instead of silently equal to a complete one.
        let controlCount = 0;
        let testCount = 0;
        let leggedTests = 0;
        for (const uc of useCases) {
          controlCount += uc.controls.length;
          for (const control of uc.controls) {
            for (const test of control.tests) {
              testCount += 1;
              if (test.evidenceLegs.length > 0) leggedTests += 1;
            }
          }
        }
        return {
          subprocess,
          useCases,
          controlCount,
          testCount,
          leggedTests,
          coverage: testCount === 0 ? 0 : Math.round((leggedTests / testCount) * 100),
          // Distinguishes "the overlay has not reached here" from "the overlay
          // measured this and found nothing" — identical arithmetic, opposite
          // meanings. See the coverage rail for why this matters at real scale.
          overlayPending: testCount > 0 && leggedTests === 0,
        };
      }),
    }));
  }, [filteredUseCases]);

  /** PIVOT view: provisional ROSI category → use cases. */
  const pivotGroups = useMemo(() => {
    const byCategory = new Map<string, LibUseCase[]>();
    for (const uc of filteredUseCases) {
      const key = uc.rosiCategory ?? UNCATEGORIZED;
      const list = byCategory.get(key) ?? [];
      list.push(uc);
      byCategory.set(key, list);
    }
    // Categorized buckets first, honest bucket last.
    return [...byCategory.entries()].sort(([a], [b]) =>
      a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b)
    );
  }, [filteredUseCases]);

  const activeUseCase = useMemo(
    () => tree?.useCases.find((u) => u.id === selectedUseCase) ?? null,
    [tree, selectedUseCase]
  );

  // The connection map is the signature element in BOTH views — surface it on
  // a worked selection instead of an empty pane (D-BANK-UX-02, extended by
  // CC-V35-ARC-UX-01). Previously pivot-only, which left the family view — the
  // richer of the two — landing on an instructional card that explained the
  // model once and then vanished on first click. Landing on a real use case
  // means the model is explained by the product itself, continuously.
  useEffect(() => {
    if (!selectedUseCase && tree && tree.useCases.length > 0) {
      setSelectedUseCase(tree.useCases[0].id);
    }
  }, [selectedUseCase, tree]);

  /**
   * Deep link (DC-1807): `/arc-library?control=CS4[&test=CS4.1]` lands on the
   * named control — the click-through target for a Results or Evidence reader
   * who has lost which control a test belongs to. Consumed ONCE on load;
   * afterwards the reader's own navigation owns the state. An unknown code
   * changes nothing — the library opens normally rather than erroring on a
   * stale link.
   */
  const [searchParams] = useSearchParams();
  const deepLinkConsumed = useRef(false);
  useEffect(() => {
    if (deepLinkConsumed.current || !tree) return;
    deepLinkConsumed.current = true;
    const controlParam = searchParams.get('control');
    if (!controlParam) return;
    for (const uc of tree.useCases) {
      const match = uc.controls.find((c) => c.controlCode === controlParam);
      if (match) {
        setView('family');
        setSelectedUseCase(uc.id);
        setOpenControl(match.id);
        return;
      }
    }
    const bundle = tree.bundleControls.find((c) => c.controlCode === controlParam);
    if (bundle) {
      const holder = tree.useCases.find((u) => u.subprocess === bundle.bundleScope);
      if (holder) {
        setView('family');
        setSelectedUseCase(holder.id);
        setOpenControl(bundle.id);
      }
    }
  }, [tree, searchParams]);

  /**
   * Which families are expanded in the picker.
   *
   * Default: exactly the one holding the current selection. Expanding another
   * is navigation, not selection — it never moves the detail pane, so a reader
   * can look around without losing their place. `manualFamilies` records the
   * ones the reader opened by hand so a later selection change does not
   * silently close them.
   */
  const [manualFamilies, setManualFamilies] = useState<Set<string>>(new Set());
  const expandedFamilies = useMemo(() => {
    const open = new Set(manualFamilies);
    if (activeUseCase) open.add(activeUseCase.subprocess);
    return open;
  }, [manualFamilies, activeUseCase]);
  const toggleFamily = useCallback((subprocess: string) => {
    setManualFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(subprocess)) next.delete(subprocess);
      else next.add(subprocess);
      return next;
    });
  }, []);

  const familyBundleControls = useMemo(() => {
    if (!tree || !activeUseCase) return [];
    return tree.bundleControls.filter((c) => c.bundleScope === activeUseCase.subprocess);
  }, [tree, activeUseCase]);

  /** Distinct families in the catalog — the version block carries no count. */
  const familyCount = useMemo(
    () => (tree ? new Set(tree.useCases.map((u) => u.subprocess)).size : 0),
    [tree]
  );

  /** How many use cases share this family — what "assessed once" is once FOR. */
  const familySiblingCount = useMemo(() => {
    if (!tree || !activeUseCase) return 0;
    return tree.useCases.filter((u) => u.subprocess === activeUseCase.subprocess).length;
  }, [tree, activeUseCase]);

  /** The open control's CODE — `openControl` holds an id; the strip shows CS1. */
  const openControlCode = useMemo(() => {
    if (!openControl || !activeUseCase) return null;
    const match = [...activeUseCase.controls, ...familyBundleControls].find(
      (c) => c.id === openControl
    );
    return match?.controlCode ?? null;
  }, [openControl, activeUseCase, familyBundleControls]);

  const renderTest = (test: LibTest): React.ReactElement => (
    <li key={test.id} className="arc-test">
      <div className="arc-test-head">
        <span className="banking-status-chip arc-code-chip">{test.testCode}</span>
        <span className="arc-test-name">{test.name}</span>
        {test.severity && <span className="arc-test-severity">{test.severity}</span>}
      </div>
      <div className="arc-test-meta">
        {test.testingCategory && <span>{test.testingCategory}</span>}
        {test.regulationsReferenced && <span>{test.regulationsReferenced}</span>}
      </div>
      <div className="arc-test-legs">
        <span className="arc-overlay-label" title="ROSI overlay — provisional, partner-confirmable">
          Evidence legs (ROSI overlay):
        </span>{' '}
        {test.evidenceLegs.length > 0 ? (
          test.evidenceLegs.map((leg) => (
            <span key={leg} className="banking-status-chip arc-leg-chip">
              {leg}
            </span>
          ))
        ) : (
          <span className="arc-not-assigned">not yet assigned</span>
        )}
      </div>
    </li>
  );

  const renderControl = (control: LibControl): React.ReactElement => (
    <section key={control.id} className="arc-control" data-testid={`arc-control-${control.controlCode}`}>
      <button
        type="button"
        className="arc-control-head"
        onClick={() => setOpenControl(openControl === control.id ? null : control.id)}
      >
        <span className="banking-status-chip arc-code-chip">{control.controlCode}</span>
        <span className="arc-control-name">{control.name}</span>
        {control.scopeLevel === 'bundle' && (
          <span className="arc-bundle-chip" title="Assessed once for the whole function">
            function-level
          </span>
        )}
        {/*
          The judge-without-opening signal. A collapsed row that shows only a
          test count tells you size but not substance; measurable-from-data
          says how much of this control Compass can actually evidence versus
          what still rests on attestation.
        */}
        <span className="arc-control-count">
          {(() => {
            const total = control.tests.length;
            if (total === 0) return 'no tests';
            const measurable = control.tests.filter((t) => t.evidenceLegs.length > 0).length;
            const plural = `${total} test${total === 1 ? '' : 's'}`;
            // 103 of 108 controls in the real catalog have no legs at all.
            // "0 of 3 measurable" states that as a measurement; it is not one.
            // The product's existing language for an unassigned leg is "not yet
            // assigned" — use it here so the collapsed row makes the same claim
            // the expanded row does.
            return measurable === 0
              ? `evidence legs not yet assigned · ${plural}`
              : `${measurable} of ${total} measurable · ${plural}`;
          })()}
        </span>
      </button>
      {openControl === control.id && (
        <div className="arc-control-body">
          {control.description && <p>{control.description}</p>}
          {(control.regulators || control.officialStandards) && (
            <p className="arc-control-reg">
              {control.regulators && <span>Regulators: {control.regulators}</span>}{' '}
              {control.officialStandards && <span>Standards: {control.officialStandards}</span>}
            </p>
          )}
          {control.raci.length > 0 && (
            <p className="arc-control-raci">
              {control.raci.map((r) => (
                <span key={r.raciRole + r.party} className="arc-raci-item">
                  <strong>{r.raciRole.replace('_', ' ')}:</strong> {r.party}
                </span>
              ))}
            </p>
          )}
          {control.tests.length > 0 ? (
            <ul className="arc-test-list">{control.tests.map(renderTest)}</ul>
          ) : (
            <p className="arc-not-assigned">No tests in this catalog version.</p>
          )}
        </div>
      )}
    </section>
  );

  return (
    <div className="banking-page arc-library" data-testid="arc-library-page">
      {/*
        The page used to open on a grey line leading with a content digest —
        infrastructure trivia in the position that should carry identity and
        scale, with the ARC/ROSI provenance split buried in a run-on sentence
        underneath. The masthead states what this is, how much of it there is,
        and whose each layer is. Counts come from the payload: the catalog is
        immutable and versioned, so a hardcoded number here would be a lie
        about a specific published version.
      */}
      <header className="arc-masthead" data-testid="arc-masthead">
        <p className="arc-mast-top">ARC LIBRARY</p>
        <div className="arc-mast-main">
          <div className="arc-mast-title">
            <h1>AI Risk &amp; Compliance Library</h1>
            <p>
              Every thread from risk to control to test to evidence — pinned to one catalog
              version.
            </p>
          </div>
          {tree && (
            <div className="arc-statrow" data-testid="arc-masthead-stats">
              {/* Families are DERIVED — the version block carries no count for
                  them — while the rest are the catalog's declared totals. */}
              <div className="arc-stat" data-testid="arc-stat-families">
                <span className="arc-stat-n">{familyCount}</span>
                <span className="arc-stat-l">
                  {familyCount === 1 ? 'Function' : 'Functions'}
                </span>
              </div>
              <div className="arc-stat" data-testid="arc-stat-usecases">
                <span className="arc-stat-n">{tree.version.useCaseCount}</span>
                <span className="arc-stat-l">Use cases</span>
              </div>
              <div className="arc-stat" data-testid="arc-stat-controls">
                <span className="arc-stat-n">{tree.version.controlCount}</span>
                <span className="arc-stat-l">Controls</span>
              </div>
              <div className="arc-stat" data-testid="arc-stat-tests">
                <span className="arc-stat-n">{tree.version.testCount}</span>
                <span className="arc-stat-l">Tests</span>
              </div>
              <div className="arc-stat" data-testid="arc-stat-risks">
                <span className="arc-stat-n">{tree.version.riskCount}</span>
                <span className="arc-stat-l">Risks</span>
              </div>
            </div>
          )}
        </div>
        <div className="arc-provbar" data-testid="arc-attribution">
          <span className="arc-attr-arc" data-testid="arc-prov-catalog">
            <i className="arc-prov-dot arc-prov-dot-src" aria-hidden="true" />
            Structure, controls &amp; tests — ARC workbook, A. Philo, pre-existing IP used under
            the partnership entitlement
          </span>
          <span className="arc-attr-rosi" data-testid="arc-prov-overlay">
            <i className="arc-prov-dot arc-prov-dot-ovl" aria-hidden="true" />
            Evidence legs, categories &amp; suggestion triggers — ROSI overlay, provisional and
            partner-confirmable
          </span>
          {tree && (
            <span className="arc-catchip" data-testid="arc-catalog-meta">
              {tree.version.versionLabel} · {tree.version.contentDigest.slice(0, 12)}…
            </span>
          )}
        </div>
      </header>

      {/*
        Catalog compile report (DC-1791 FR-1.3): the seed's own accounting of
        every workbook row, in the product rather than a terminal scrollback.
        Admin-only and collapsed — it is a seed-quality surface, not part of
        browsing the catalog.
      */}
      {state.phase === 'ready' && isAdmin && (
        <section className="arc-compile-report-section">
          <button
            type="button"
            className="arc-compile-report-toggle"
            data-testid="arc-compile-report-toggle"
            aria-expanded={reportOpen}
            onClick={() => void toggleReport()}
          >
            <span aria-hidden="true">{reportOpen ? '▾' : '▸'}</span> Catalog compile report
          </button>
          {reportOpen && reportState.phase === 'loading' && (
            <p className="arc-compile-report-note" aria-busy="true">
              Reading the compile report…
            </p>
          )}
          {reportOpen && reportState.phase === 'error' && (
            <p className="arc-compile-report-note" role="alert">
              The compile report could not be read.
            </p>
          )}
          {reportOpen && reportState.phase === 'loaded' && reportState.report === null && (
            <p className="arc-compile-report-note" data-testid="arc-compile-report-missing">
              No compile report was recorded for this catalog version — it was seeded by an
              earlier compiler. Re-seeding under a new version label records one.
            </p>
          )}
          {reportOpen && reportState.phase === 'loaded' && reportState.report !== null && (
            <div className="arc-compile-report" data-testid="arc-compile-report">
              <p className="arc-compile-report-sum">
                <strong>{reportState.report.sourceRowCount} source rows</strong> ={' '}
                {reportState.report.seedable.count} seedable +{' '}
                {reportState.report.unseedable.count} unseedable
              </p>
              <p className="arc-compile-report-bands">
                {Object.entries(reportState.report.seedable.byBandForm).map(([form, n]) => (
                  <span key={form} className="arc-compile-report-band">
                    {form.replace(/_/g, ' ')}: <strong>{n}</strong>
                  </span>
                ))}
              </p>
              <p className="arc-compile-report-legs" data-testid="arc-compile-report-legs">
                Evidence legs from <strong>{reportState.report.legIngestion.source}</strong> —{' '}
                {reportState.report.legIngestion.classifiedTests} tests classified
                {reportState.report.legIngestion.unseedableEntries > 0 &&
                  `, ${reportState.report.legIngestion.unseedableEntries} entries matched unseedable rows`}
              </p>
              {reportState.report.unseedable.count === 0 ? (
                <p
                  className="arc-compile-report-note"
                  data-testid="arc-compile-report-unseedable-empty"
                >
                  Nothing was skipped — every source row seeded.
                </p>
              ) : (
                <div data-testid="arc-compile-report-unseedable">
                  <p className="arc-compile-report-note">
                    Unseedable rows (no attachable control ID
                    {Object.keys(reportState.report.unseedable.weightSplit).length > 0 &&
                      `; weights ${Object.entries(reportState.report.unseedable.weightSplit)
                        .map(([w, n]) => `${w} ×${n}`)
                        .join(', ')}`}
                    ) — reported, never seeded, never silent:
                  </p>
                  <table className="arc-compile-report-table">
                    <thead>
                      <tr>
                        <th scope="col">Row</th>
                        <th scope="col">Control label</th>
                        <th scope="col">Test</th>
                        <th scope="col">Subprocess</th>
                        <th scope="col">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportState.report.unseedable.rows.map((row) => (
                        <tr key={row.rowNumber}>
                          <td>{row.rowNumber}</td>
                          <td>{row.controlRef}</td>
                          <td>{row.name}</td>
                          <td>{row.subprocess ?? '—'}</td>
                          <td>{row.weightText ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {state.phase === 'loading' && (
        <div className="banking-grid-skeleton" aria-busy="true" aria-label="Loading the Library">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="banking-skeleton-row" />
          ))}
        </div>
      )}

      {state.phase === 'error' && (
        <div className="banking-grid-error" role="alert">
          <p>The Library could not be loaded.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {state.phase === 'not_entitled' && (
        <div className="banking-grid-empty" data-testid="arc-not-entitled">
          <h2>No ARC catalog is active for this organization</h2>
          <p>
            The Library serves the versioned ARC catalog your organization is entitled to.
            Nothing is shown until an administrator activates a published catalog version.
          </p>
          {isAdmin && state.status.availableVersions.length > 0 && (
            <div className="arc-activate" data-testid="arc-activate">
              <select
                aria-label="Catalog version"
                value={chosenVersion}
                onChange={(e) => setChosenVersion(e.target.value)}
              >
                <option value="">Choose a catalog version…</option>
                {state.status.availableVersions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.versionLabel} — {v.sourceLabel}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="banking-btn banking-btn-primary"
                data-testid="arc-activate-button"
                disabled={!chosenVersion || activating}
                onClick={() => void activate()}
              >
                {activating ? 'Activating…' : 'Activate for this organization'}
              </button>
              {activateError && (
                <div className="banking-create-error" role="alert">
                  {activateError}
                </div>
              )}
            </div>
          )}
          {isAdmin && state.status.availableVersions.length === 0 && (
            <p>
              No catalog versions are published on this deployment yet — the operator seeds one
              with the ARC catalog seeder from the authorized workbook source.
            </p>
          )}
        </div>
      )}

      {state.phase === 'ready' && tree && (
        <div className="arc-browser">
          <aside className="arc-nav" data-testid="arc-family-nav">
            <div className="arc-view-toggle" role="tablist" aria-label="Library view">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'family'}
                className={view === 'family' ? 'active' : ''}
                data-testid="arc-view-family"
                onClick={() => setView('family')}
              >
                By function
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'pivot'}
                className={view === 'pivot' ? 'active' : ''}
                data-testid="arc-view-pivot"
                onClick={() => setView('pivot')}
              >
                By AI use case
              </button>
            </div>
            <input
              type="search"
              className="arc-search"
              placeholder="Search use cases and control codes…"
              aria-label="Search the Library"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            {view === 'family' &&
              familyGroups.map((group) => (
                <div key={group.keyProcess} className="arc-nav-group">
                  <p className="arc-nav-process">{group.keyProcess}</p>
                  {group.families.map((family) => {
                    const open = expandedFamilies.has(family.subprocess);
                    return (
                      <div key={family.subprocess} className="arc-nav-family">
                        <button
                          type="button"
                          className={`arc-nav-family-head ${open ? 'open' : ''}`}
                          data-testid={`arc-family-head-${family.subprocess}`}
                          aria-expanded={open}
                          onClick={() => toggleFamily(family.subprocess)}
                        >
                          <span className="arc-nav-family-row">
                            <span className="arc-nav-caret" aria-hidden="true">
                              {open ? '▾' : '▸'}
                            </span>
                            {/* Family names reach 56 characters; a wrapped name
                                breaks the one-row-per-family promise the whole
                                collapsed nav rests on. */}
                            <span className="arc-nav-family-name" title={family.subprocess}>
                              {family.subprocess}
                            </span>
                          </span>
                          <span className="arc-nav-family-sum">
                            {family.useCases.length} use case
                            {family.useCases.length === 1 ? '' : 's'} · {family.controlCount}{' '}
                            control{family.controlCount === 1 ? '' : 's'}
                          </span>
                          {/*
                            ABSENCE IS NOT A MEASURED ZERO. In the real ARC v6
                            catalog the ROSI overlay has reached one family of
                            seventeen, so a plain percentage bar renders as
                            sixteen empty rails — which reads as "this
                            framework has no evidence behind it" when the
                            incompleteness is entirely in OUR overlay layer.
                            An unreached family gets a dashed pending rail and
                            says whose gap it is; a reached one shows its real
                            fraction and stands out as the worked example.
                          */}
                          <span
                            className="arc-nav-cov"
                            data-testid={`arc-family-coverage-${family.subprocess}`}
                            data-coverage={family.overlayPending ? 'pending' : family.coverage}
                            title={
                              family.overlayPending
                                ? `ROSI overlay not yet extended to this function — none of its ${family.testCount} tests carry an evidence leg yet. The ARC controls and tests are complete; the overlay is ours to extend.`
                                : `Evidence legs assigned on ${family.leggedTests} of ${family.testCount} tests (ROSI overlay)`
                            }
                          >
                            {family.overlayPending ? (
                              <span className="arc-nav-cov-pending" aria-hidden="true" />
                            ) : (
                              <span
                                className={`arc-nav-cov-fill ${family.coverage < 60 ? 'thin' : ''}`}
                                style={{ width: `${family.coverage}%` }}
                              />
                            )}
                          </span>
                        </button>
                        {open &&
                          family.useCases.map((uc) => (
                            <button
                              key={uc.id}
                              type="button"
                              className={`arc-nav-item ${uc.id === selectedUseCase ? 'active' : ''}`}
                              /*
                                Use-case names run to 137 characters in the real
                                catalog (median 30, p90 84). The row truncates
                                visually; the title keeps the full name reachable
                                so truncation never hides what a row is.
                              */
                              title={uc.name}
                              onClick={() => {
                                setSelectedUseCase(uc.id);
                                setOpenControl(null);
                              }}
                            >
                              {uc.name}
                            </button>
                          ))}
                      </div>
                    );
                  })}
                </div>
              ))}

            {view === 'pivot' &&
              pivotGroups.map(([category, useCases]) => (
                <div key={category} className="arc-nav-group">
                  <p className="arc-nav-process">
                    {category} <span className="arc-nav-count">{useCases.length}</span>
                    {category !== UNCATEGORIZED && (
                      <span
                        className="arc-overlay-label"
                        title="Provisional ROSI category overlay"
                      >
                        {' '}
                        · ROSI overlay
                      </span>
                    )}
                  </p>
                  {useCases.map((uc) => (
                    <button
                      key={uc.id}
                      type="button"
                      className={`arc-nav-item ${uc.id === selectedUseCase ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedUseCase(uc.id);
                        setOpenControl(null);
                      }}
                    >
                      {uc.name}
                      <span className="arc-nav-item-family">{uc.subprocess}</span>
                    </button>
                  ))}
                </div>
              ))}
          </aside>

          <main className="arc-main">
            {!activeUseCase && (
              <div className="banking-grid-empty">
                <h2>Browse the entitled catalog</h2>
                <p>
                  Pick an AI use case on the left — by function (the traditional bank view) or by
                  AI use-case category. Each use case shows its controls; each control opens
                  into its tests and evidence legs.
                </p>
              </div>
            )}

            {activeUseCase && (
              <article data-testid="arc-use-case-detail">
                <OrientationStrip
                  useCase={activeUseCase}
                  openControlCode={openControlCode}
                />
                <p className="banking-eyebrow">
                  {activeUseCase.keyProcess} · {activeUseCase.subprocess}
                </p>
                <h2>{activeUseCase.name}</h2>
                <p className="arc-scoping-link">
                  <Link to="/use-cases">
                    Scope your inventoried use cases against these controls →
                  </Link>
                </p>
                {/*
                  Rendered in BOTH views. It was previously gated to `pivot`,
                  which hid the Library's best explanation behind the one view
                  that is 81-of-85 uncategorized — so a reader in the family
                  view, the rich one, never saw the model explained at all.
                */}
                {tree && (
                  <ConnectionMap
                    useCase={activeUseCase}
                    bundleControls={familyBundleControls}
                    risks={tree.risks}
                  />
                )}
                {activeUseCase.rosiCategory && (
                  <p className="arc-overlay-label">
                    Category (ROSI overlay, provisional): {activeUseCase.rosiCategory}
                  </p>
                )}
                {/*
                  Five prose blocks used to stack here at identical size,
                  weight and colour — a form dump the reader had to wade
                  through before reaching anything actionable. Folded into one
                  disclosure, collapsed on arrival: the map and the controls
                  come first, and the narrative is one click away for whoever
                  wants it.
                */}
                <details className="arc-why" data-testid="arc-why-matters">
                  <summary>
                    Why this use case matters
                    <span className="arc-why-hint">
                      purpose, mechanism, and what goes wrong
                    </span>
                  </summary>
                  <div className="arc-why-body">
                    {activeUseCase.subprocessPurpose && (
                      <p>{activeUseCase.subprocessPurpose}</p>
                    )}
                    {activeUseCase.goals && (
                      <p>
                        <strong>Goals:</strong> {activeUseCase.goals}
                      </p>
                    )}
                    {activeUseCase.modelDescription && (
                      <p>
                        <strong>How it works:</strong> {activeUseCase.modelDescription}
                      </p>
                    )}
                    {activeUseCase.keyRisks && (
                      <p>
                        <strong>Key risks:</strong> {activeUseCase.keyRisks}
                      </p>
                    )}
                    {activeUseCase.systemicRisk && (
                      <p>
                        <strong>Systemic risk:</strong> {activeUseCase.systemicRisk}
                      </p>
                    )}
                  </div>
                </details>

                {/* A count is not a relationship. Say what a control IS. */}
                <h3 className="arc-section-head" data-testid="arc-controls-head">
                  Controls{' '}
                  <span className="arc-section-rel">
                    — what ARC requires this use case to satisfy.{' '}
                    {activeUseCase.controls.length} control
                    {activeUseCase.controls.length === 1 ? '' : 's'}.
                  </span>
                </h3>
                {activeUseCase.controls.length > 0 ? (
                  activeUseCase.controls.map(renderControl)
                ) : (
                  <p className="arc-not-assigned">
                    No use-case-level controls in this catalog version.
                  </p>
                )}

                {familyBundleControls.length > 0 && (
                  <>
                    <h3 className="arc-section-head">
                      Function-level controls{' '}
                      <span className="arc-nav-count">{familyBundleControls.length}</span>
                    </h3>
                    {/*
                      A `family-level` chip explains nothing to a reader who
                      does not already know what a bundle scope is. Say the
                      relationship outright.
                    */}
                    <p className="arc-family-scope-note" data-testid="arc-family-scope-note">
                      {familyBundleControls.length === 1 ? 'This control applies' : 'These controls apply'}{' '}
                      to every use case in <strong>{activeUseCase.subprocess}</strong> — assessed
                      once, and the result is referenced by all{' '}
                      {familySiblingCount} rather than re-derived for each.
                    </p>
                    {familyBundleControls.map(renderControl)}
                  </>
                )}
              </article>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ArcLibrary;
